/**
 * Studio → Bundles → Detail — #152.
 *
 * Detail page for a single bundle. Shows the full item list grouped by
 * kind (tools / skills / prompts / agents / mcp / kb), the publish
 * status, and the install button (for published bundles).
 *
 * Buttons rendered conditionally:
 *   - draft + own bundle      → Publish, Edit, Archive
 *   - published + any bundle  → Install
 *   - published + own bundle  → Install, Archive
 *   - archived                → no actions
 *
 * Install POSTs /v1/bundles/:id/install which clones every item row
 * into the requesting tenant's scope and returns a summary the UI
 * surfaces in a toast.
 */

import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  ArrowLeft, Package, Download, CheckCircle2, Lock, AlertTriangle,
  Wrench, BookOpen, MessageSquare, Bot, Plug, FileText,
  Archive, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  useBundle,
  useInstallBundle,
  usePublishBundle,
  useArchiveBundle,
  type BundleEntityKind,
} from '@/lib/use-studio-bundles';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/studio/bundles/$id')({
  component: StudioBundleDetailPage,
});

// ─── Entity-kind presentation ───────────────────────────────────────────

const KIND_META: Record<
  BundleEntityKind,
  { label: string; cls: string; icon: typeof Wrench }
> = {
  tool: {
    label: 'Tool',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    icon: Wrench,
  },
  skill: {
    label: 'Skill',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: BookOpen,
  },
  prompt: {
    label: 'Prompt',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: MessageSquare,
  },
  agent: {
    label: 'Agent',
    cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
    icon: Bot,
  },
  mcp: {
    label: 'MCP Server',
    cls: 'bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30',
    icon: Plug,
  },
  kb: {
    label: 'Knowledge',
    cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    icon: FileText,
  },
};

const STATUS_META: Record<
  string,
  { label: string; cls: string; icon: typeof CheckCircle2 }
> = {
  draft: {
    label: 'Draft',
    cls: 'bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/30',
    icon: Sparkles,
  },
  published: {
    label: 'Published',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: CheckCircle2,
  },
  archived: {
    label: 'Archived',
    cls: 'bg-slate-400/10 text-slate-500 dark:text-slate-400 border-slate-400/30',
    icon: Archive,
  },
};

function StudioBundleDetailPage() {
  const { id } = useParams({ from: '/studio/bundles/$id' });
  const navigate = useNavigate();

  const { data, isLoading, isError } = useBundle(id);
  const installMutation = useInstallBundle(id);
  const publishMutation = usePublishBundle(id);
  const archiveMutation = useArchiveBundle(id);

  const grouped = useMemo(() => {
    const items = data?.data.items ?? [];
    const map: Record<BundleEntityKind, typeof items> = {
      tool: [], skill: [], prompt: [], agent: [], mcp: [], kb: [],
    };
    for (const it of items) map[it.entityKind as BundleEntityKind]?.push(it);
    return map;
  }, [data]);

  if (isLoading) {
    return (
      <div className="p-8 text-sm text-muted-foreground">Loading bundle…</div>
    );
  }
  if (isError || !data?.data) {
    return (
      <div className="p-8 text-sm text-muted-foreground">
        <AlertTriangle className="h-5 w-5 inline mr-2 text-amber-500" />
        Bundle not found or failed to load.
      </div>
    );
  }

  const b = data.data;
  const statusMeta = STATUS_META[b.status] ?? STATUS_META.draft;
  const StatusIcon = statusMeta.icon;

  const handleInstall = async () => {
    try {
      const r = await installMutation.mutateAsync();
      const lines = [`Installed ${r.installed} item(s)`];
      if (r.skipped > 0) lines.push(`skipped ${r.skipped}`);
      if (r.deferred > 0) lines.push(`deferred ${r.deferred} (kind not yet supported)`);
      toast.success(`Bundle installed — ${lines.join(', ')}`);
      navigate({ to: '/studio' });
    } catch (e) {
      toast.error(`Install failed: ${(e as Error).message}`);
    }
  };

  const handlePublish = async () => {
    try {
      await publishMutation.mutateAsync();
      toast.success('Bundle published. Manifest frozen.');
    } catch (e) {
      toast.error(`Publish failed: ${(e as Error).message}`);
    }
  };

  const handleArchive = async () => {
    if (!confirm(`Archive bundle "${b.name}"? It will be hidden from the bundle list.`)) {
      return;
    }
    try {
      await archiveMutation.mutateAsync();
      toast.success('Bundle archived.');
      navigate({ to: '/studio/bundles' });
    } catch (e) {
      toast.error(`Archive failed: ${(e as Error).message}`);
    }
  };

  const canEdit = !b.isReadOnly && b.status === 'draft';
  const canPublish = !b.isReadOnly && b.status === 'draft' && b.itemCount > 0;
  const canArchive = !b.isReadOnly && b.status !== 'archived';
  const canInstall = b.status === 'published';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-card/50 backdrop-blur">
        <div className="flex items-center gap-2 mb-2">
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={() => navigate({ to: '/studio/bundles' })}
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Bundles
          </Button>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-5 w-5 text-primary" />
              <h1 className="text-lg font-semibold">{b.name}</h1>
              <Badge variant="outline" className={cn('gap-1', statusMeta.cls)}>
                <StatusIcon className="h-3 w-3" />
                {statusMeta.label}
              </Badge>
              {b.author === 'platform' && (
                <Badge
                  variant="outline"
                  className="gap-1 bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
                >
                  <Lock className="h-3 w-3" /> Platform
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground mb-1">
              {b.slug} · v{b.version} · industry: {b.industryTag} · {b.itemCount} items
            </div>
            <p className="text-sm text-muted-foreground max-w-3xl">{b.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {canInstall && (
              <Button
                size="sm"
                onClick={handleInstall}
                disabled={installMutation.isPending}
                className="gap-1.5"
                title="Clone every item in this bundle into your workspace."
              >
                <Download className="h-3.5 w-3.5" />
                {installMutation.isPending ? 'Installing…' : 'Install'}
              </Button>
            )}
            {canPublish && (
              <Button
                size="sm"
                variant="default"
                onClick={handlePublish}
                disabled={publishMutation.isPending}
                className="gap-1.5"
                title="Freeze the manifest and make this bundle installable."
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                {publishMutation.isPending ? 'Publishing…' : 'Publish'}
              </Button>
            )}
            {canEdit && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                title="Edit bundle metadata and items."
                onClick={() => navigate({
                  to: '/studio/bundles/new',
                  search: { editId: b.id },
                })}
              >
                Edit
              </Button>
            )}
            {canArchive && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleArchive}
                disabled={archiveMutation.isPending}
                className="gap-1.5"
                title="Archive this bundle (hides it from the list)."
              >
                <Archive className="h-3.5 w-3.5" /> Archive
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Item list */}
      <div className="flex-1 overflow-auto p-4 space-y-3">
        {b.items.length === 0 && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No items in this bundle yet. Edit the draft to add catalog entities.
            </CardContent>
          </Card>
        )}
        {(Object.keys(KIND_META) as BundleEntityKind[]).map((kind) => {
          const items = grouped[kind];
          if (!items || items.length === 0) return null;
          const meta = KIND_META[kind];
          const Icon = meta.icon;
          return (
            <Card key={kind}>
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={cn('gap-1', meta.cls)}>
                    <Icon className="h-3 w-3" />
                    {meta.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {items.length} item{items.length === 1 ? '' : 's'}
                  </span>
                </div>
                <ul className="text-sm space-y-1.5">
                  {items.map((it) => {
                    const snap = (it.entityVersionSnapshot ?? {}) as {
                      name?: string;
                      displayName?: string;
                      description?: string;
                    };
                    return (
                      <li key={it.id} className="border-l-2 border-muted pl-2">
                        <div className="font-medium">
                          {snap.displayName || snap.name || it.entityId.slice(0, 8)}
                        </div>
                        {snap.description && (
                          <div className="text-xs text-muted-foreground">
                            {snap.description.slice(0, 200)}
                            {snap.description.length > 200 ? '…' : ''}
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
                          id: {it.entityId}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
